import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useApi } from '../lib/api';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface Analytics {
  period: { start: string; end: string };
  overall: {
    totalSessions: number;
    avgCoverage: string;
    totalDurationMs: number;
    avgDurationMs: number;
  };
  memberCount: number;
  memberStats: {
    userId: string;
    name: string | null;
    email: string;
    sessionCount: number;
    avgCoverage: string;
    totalDurationMs: number;
  }[];
}

interface RecentSession {
  id: string;
  title: string;
  status: string;
  startedAt: string;
  coverageScore: number | null;
  user: {
    id: string;
    name: string | null;
    email: string;
    avatarUrl: string | null;
  };
}

interface ChartData {
  dailyStats: {
    date: string;
    sessionCount: number;
    avgCoverage: string;
  }[];
  memberPerformance: {
    userId: string;
    name: string;
    sessionCount: number;
    avgCoverage: string;
  }[];
}

export function OrgDashboard() {
  const { organization } = useOrganization();
  const { get } = useApi();

  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [recentSessions, setRecentSessions] = useState<RecentSession[]>([]);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!organization) return;

      try {
        setLoading(true);
        setError(null);

        const [analyticsRes, sessionsRes, chartRes] = await Promise.all([
          get<Analytics>(`/organizations/${organization.id}/analytics`),
          get<{ sessions: RecentSession[] }>(`/organizations/${organization.id}/sessions?limit=5`),
          get<ChartData>(`/organizations/${organization.id}/analytics/chart?days=14`),
        ]);

        setAnalytics(analyticsRes);
        setRecentSessions(sessionsRes.sessions);
        setChartData(chartRes);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [organization]);

  function formatDuration(ms: number | null | undefined): string {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  }

  function getRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  if (!organization) {
    return (
      <div className="bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
        <p className="text-slate-400">Organization context required</p>
        <p className="mt-2 text-sm text-slate-500">
          Select an organization to view the manager dashboard
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  // Sort members by avg coverage for leaderboard
  const topPerformers = analytics?.memberStats
    .filter((m) => m.sessionCount > 0)
    .sort((a, b) => parseFloat(b.avgCoverage) - parseFloat(a.avgCoverage))
    .slice(0, 5) || [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Manager Dashboard</h1>
          <p className="mt-1 text-slate-400">
            Team performance overview for {organization.name}
          </p>
        </div>
        <Link
          to="/org/sessions"
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-md transition-colors"
        >
          View All Sessions
        </Link>
      </div>

      {/* Stats Overview */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Sessions"
            value={analytics.overall.totalSessions.toString()}
            subtext="Last 30 days"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />
          <StatCard
            label="Team Avg Coverage"
            value={`${analytics.overall.avgCoverage}%`}
            subtext="Across all sessions"
            valueColor={
              parseFloat(analytics.overall.avgCoverage) >= 80
                ? 'text-green-500'
                : parseFloat(analytics.overall.avgCoverage) >= 50
                ? 'text-yellow-500'
                : 'text-red-500'
            }
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Total Practice Time"
            value={formatDuration(analytics.overall.totalDurationMs)}
            subtext="Combined team time"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Team Members"
            value={analytics.memberCount.toString()}
            subtext="Active in organization"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Charts Section */}
      {chartData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sessions Over Time */}
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
            <h2 className="text-lg font-medium text-white mb-4">Sessions & Coverage (14 days)</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData.dailyStats}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorCoverage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    fontSize={12}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#64748b"
                    fontSize={12}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#64748b"
                    fontSize={12}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '0.5rem',
                    }}
                    labelStyle={{ color: '#94a3b8' }}
                    formatter={(value, name) => [
                      name === 'avgCoverage' ? `${value}%` : value,
                      name === 'avgCoverage' ? 'Avg Coverage' : 'Sessions',
                    ]}
                    labelFormatter={(label) => {
                      const date = new Date(label);
                      return date.toLocaleDateString();
                    }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={(value) =>
                      value === 'sessionCount' ? 'Sessions' : 'Avg Coverage'
                    }
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="sessionCount"
                    stroke="#22c55e"
                    fillOpacity={1}
                    fill="url(#colorSessions)"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgCoverage"
                    stroke="#3b82f6"
                    fillOpacity={1}
                    fill="url(#colorCoverage)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Member Performance Bar Chart */}
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
            <h2 className="text-lg font-medium text-white mb-4">Team Performance Comparison</h2>
            <div className="h-64">
              {chartData.memberPerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData.memberPerformance}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 60, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      stroke="#64748b"
                      fontSize={12}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke="#64748b"
                      fontSize={12}
                      width={50}
                      tickFormatter={(value) => value.length > 8 ? `${value.slice(0, 8)}...` : value}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        borderRadius: '0.5rem',
                      }}
                      labelStyle={{ color: '#94a3b8' }}
                      formatter={(value, name) => [
                        name === 'avgCoverage' ? `${value}%` : value,
                        name === 'avgCoverage' ? 'Avg Coverage' : 'Sessions',
                      ]}
                    />
                    <Bar
                      dataKey="avgCoverage"
                      fill="#22c55e"
                      radius={[0, 4, 4, 0]}
                      name="avgCoverage"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <p>No session data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Two column layout for leaderboard and recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-medium text-white">Top Performers</h2>
            <span className="text-xs text-slate-500">By avg coverage</span>
          </div>
          {topPerformers.length > 0 ? (
            <div className="divide-y divide-slate-700">
              {topPerformers.map((member, index) => (
                <div
                  key={member.userId}
                  className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0
                          ? 'bg-yellow-500/20 text-yellow-500'
                          : index === 1
                          ? 'bg-slate-400/20 text-slate-400'
                          : index === 2
                          ? 'bg-orange-500/20 text-orange-500'
                          : 'bg-slate-600 text-slate-400'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {member.name || member.email.split('@')[0]}
                      </p>
                      <p className="text-sm text-slate-500">
                        {member.sessionCount} session{member.sessionCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-xl font-bold ${
                        parseFloat(member.avgCoverage) >= 80
                          ? 'text-green-500'
                          : parseFloat(member.avgCoverage) >= 50
                          ? 'text-yellow-500'
                          : 'text-red-500'
                      }`}
                    >
                      {member.avgCoverage}%
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDuration(member.totalDurationMs)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <p className="text-slate-400">No sessions recorded yet</p>
              <p className="mt-2 text-sm text-slate-500">
                Team members will appear here once they complete sessions
              </p>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-medium text-white">Recent Activity</h2>
            <Link
              to="/org/sessions"
              className="text-sm text-green-500 hover:text-green-400"
            >
              View all
            </Link>
          </div>
          {recentSessions.length > 0 ? (
            <div className="divide-y divide-slate-700">
              {recentSessions.map((session) => {
                const score = Number(session.coverageScore ?? 0);
                const scoreColor =
                  score >= 80 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500';

                return (
                  <Link
                    key={session.id}
                    to={`/sessions/${session.id}`}
                    className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors block"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      {session.user.avatarUrl ? (
                        <img
                          src={session.user.avatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-400 flex-shrink-0">
                          {(session.user.name || session.user.email)[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">
                          {session.title || 'Untitled Session'}
                        </p>
                        <p className="text-sm text-slate-500 truncate">
                          {session.user.name || session.user.email} &middot; {getRelativeTime(session.startedAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-4">
                      <span className={`text-lg font-bold ${scoreColor}`}>
                        {score.toFixed(0)}%
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <p className="text-slate-400">No recent sessions</p>
              <p className="mt-2 text-sm text-slate-500">
                Sessions from your team will appear here
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/org/sessions"
          className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-slate-600 transition-all group"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-medium">View All Sessions</h3>
              <p className="text-sm text-slate-500">Browse team session history</p>
            </div>
          </div>
        </Link>
        <Link
          to="/teams"
          className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-slate-600 transition-all group"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-medium">Manage Teams</h3>
              <p className="text-sm text-slate-500">Organize team members</p>
            </div>
          </div>
        </Link>
        <Link
          to="/templates"
          className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-slate-600 transition-all group"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/10 rounded-lg group-hover:bg-green-500/20 transition-colors">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-medium">Team Templates</h3>
              <p className="text-sm text-slate-500">Shared agenda templates</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  subtext,
  valueColor = 'text-white',
  icon,
}: {
  label: string;
  value: string;
  subtext: string;
  valueColor?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-400">{label}</p>
        {icon && <div className="text-slate-500">{icon}</div>}
      </div>
      <p className={`text-3xl font-bold mt-2 ${valueColor}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-1">{subtext}</p>
    </div>
  );
}
